// packages/ui/src/contexts/UIContext.tsx
'use client';

import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Snackbar,
  Alert,
  Box,
  Button,
  CircularProgress,
  Backdrop,
  IconButton,
} from '@mui/material';
import type { AlertColor } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslations } from 'next-intl';
import { AlertBox } from '@tourtask/ui/components/AlertBox';
import { GeneralDialog } from '@tourtask/ui/components/GeneralDialog';
import type { GeneralDialogType } from '@tourtask/ui/components/GeneralDialog';
import type { CommonKey } from '@tourtask/i18n/keys';
import { sharedDialogAfterRequest, type ShowDialogArgs } from './sharedDialog';
import { useOlderReleaseRecovery } from './useOlderReleaseRecovery';

type SnackbarOptions =
  | { msgCode?: undefined; message?: undefined; content?: undefined; severity?: AlertColor }
  | { msgCode: CommonKey; message?: never; content?: never; severity?: AlertColor }
  | { message: string; msgCode?: never; content?: never; severity?: AlertColor }
  // Pre-rendered rich content (e.g. a translated string with a bold name via `t.rich`).
  | { content: ReactNode; msgCode?: never; message?: never; severity?: AlertColor };

type LoadingSnackbarOptions =
  | { msgCode: CommonKey; message?: never }
  | { message: string; msgCode?: never };

type LoadingSnackbarHandle = {
  /** Close the loading snackbar without showing a result. */
  dismiss: () => void;
  /** Close the loading snackbar and show a follow-up result snackbar. */
  resolve: (opts?: SnackbarOptions) => void;
};

type UIContextType = {
  showSnackbar: (opts?: SnackbarOptions) => void;
  /**
   * Show a snackbar with a spinner that stays open until you call `dismiss()` or `resolve()`.
   * Use for long-running operations like generating an export.
   */
  showLoadingSnackbar: (opts: LoadingSnackbarOptions) => LoadingSnackbarHandle;
  /**
   * Show the shared dialog. It holds one dialog — the last one requested — unless the open one
   * is PINNED (`ShowDialogArgs.pinned`, `sharedDialog.ts`), which a later unpinned request does not
   * replace until the operator closes it.
   */
  showDialog: (args: ShowDialogArgs) => void;
  setGlobalLoading: (loading: boolean) => void;
  // NOTE: this context intentionally exposes NO JS breakpoint flags
  // (isMdDown & co. were removed). A useMediaQuery-backed flag is `false`
  // during SSR + the hydration pass, so anything rendered from it paints
  // desktop-first on phones and visibly flips after hydration (the old
  // sidebar/hamburger flash). For responsive show/hide, use the CSS SSOT
  // helpers in `@tourtask/ui/styles/responsiveDisplaySx` instead — media
  // queries apply on the very first paint. Full rationale lives there.
};

/** The shared dialog's state: what it shows, and whether it is pinned (`sharedDialog.ts`). */
interface SharedDialogState {
  open: boolean;
  title: string;
  content: ReactNode;
  type?: GeneralDialogType;
  pinned: boolean;
}

const UIContext = createContext<UIContextType | undefined>(undefined);

export const UIProvider = ({ children }: { children: ReactNode }) => {
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    content: ReactNode;
    severity: AlertColor;
  }>({
    open: false,
    content: '',
    severity: 'success',
  });

  const [loadingSnackbar, setLoadingSnackbar] = useState<{
    open: boolean;
    message: string;
  }>({ open: false, message: '' });

  const [dialog, setDialog] = useState<SharedDialogState>({
    open: false,
    title: '',
    content: null,
    type: 'info',
    pinned: false,
  });

  const [loading, setLoading] = useState(false);

  // A page from before a deploy reloads itself when nothing can be lost; otherwise this notice.
  const { showNotice: showUpdatedNotice } = useOlderReleaseRecovery();
  const [updatedNoticeClosed, setUpdatedNoticeClosed] = useState(false);

  const t = useTranslations('common');

  // Everything handed out through the context value must be referentially
  // stable: UIProvider wraps the root layout's RSC children, so every
  // RealtimeProvider router.refresh() re-renders it, and per-render closures
  // here minted a new `showDialog` (and thereby a new `requestAction` in
  // useActionRequest, which is keyed on it) on every refresh — re-firing any
  // downstream effect keyed on them and wiping in-progress dialog/form
  // state. `t` itself churns on refresh (next-intl re-derives it from the
  // re-serialized RSC messages), so the callbacks read the latest `t`
  // through a ref instead of listing it as a dep. See AGENTS.md
  // § "Dialog/Form Client State vs router.refresh()".
  const tRef = useRef(t);
  tRef.current = t;

  const getDefaultDialogTitle = useCallback((type: GeneralDialogType | undefined): string => {
    const t = tRef.current;
    switch (type) {
      case 'success':
        return t('Success');
      case 'error':
        return t('Error');
      case 'warning':
        return t('Warning');
      case 'info':
        return t('Information');
      default:
        return t('Success');
    }
  }, []);

  const getDefaultMessage = useCallback((severity: AlertColor): string => {
    const t = tRef.current;
    switch (severity) {
      case 'success':
        return t('Changes_saved_EXCL');
      case 'error':
        return t('Error');
      case 'warning':
        return t('Warning');
      case 'info':
        return t('Information');
      default:
        console.error('Unknown severity type:', severity);
        return '';
    }
  }, []);

  // ✅ Fixed signature: accepts undefined, and defaults to {}
  const showSnackbar = useCallback<UIContextType['showSnackbar']>((opts = {}) => {
    const t = tRef.current;
    const severity: AlertColor =
      'severity' in opts && opts.severity ? opts.severity : 'success';

    const content: ReactNode =
      'content' in opts && opts.content !== undefined
        ? opts.content
        : 'msgCode' in opts && opts.msgCode
          ? t(opts.msgCode)
          : 'message' in opts && opts.message
            ? opts.message
            : getDefaultMessage(severity);

    setSnackbar({
      open: true,
      content,
      severity,
    });
  }, [getDefaultMessage]);

  const showLoadingSnackbar = useCallback<UIContextType['showLoadingSnackbar']>((opts) => {
    const message = 'msgCode' in opts && opts.msgCode ? tRef.current(opts.msgCode) : (opts as { message: string }).message;
    setLoadingSnackbar({ open: true, message });

    const dismiss = () => setLoadingSnackbar((s) => ({ ...s, open: false }));

    return {
      dismiss,
      resolve: (resultOpts) => {
        dismiss();
        showSnackbar(resultOpts);
      },
    };
  }, [showSnackbar]);

  const showDialog = useCallback<UIContextType['showDialog']>(({
    contentCode,
    extraContent,
    titleCode,
    type,
    pinned = false,
  }) => {
    const t = tRef.current;
    const requested: SharedDialogState = {
      open: true,
      title: titleCode ? t(titleCode) : getDefaultDialogTitle(type),
      content:
        extraContent === undefined ? (
          t(contentCode)
        ) : (
          <>
            {t(contentCode)}
            {extraContent}
          </>
        ),
      type,
      pinned,
    };
    // A pinned dialog (Page out of date) outlives the caller's own copy of the same failure.
    setDialog((current) => sharedDialogAfterRequest(current, requested));
  }, [getDefaultDialogTitle]);

  const setGlobalLoading = useCallback<UIContextType['setGlobalLoading']>((value) => {
    setLoading(value);
  }, []);

  // Stable object identity — a fresh literal here would defeat the stable
  // callbacks above for consumers that depend on the context value itself.
  const contextValue = useMemo<UIContextType>(
    () => ({
      showSnackbar,
      showLoadingSnackbar,
      setGlobalLoading,
      showDialog,
    }),
    [showSnackbar, showLoadingSnackbar, setGlobalLoading, showDialog],
  );

  return (
    <UIContext.Provider value={contextValue}>
      {children}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.content}
        </Alert>
      </Snackbar>

      <Snackbar
        open={loadingSnackbar.open}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          variant="filled"
          icon={false}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={18} thickness={5} sx={{ color: 'inherit' }} />
            {loadingSnackbar.message}
          </Box>
        </Alert>
      </Snackbar>

      {/* TourTask has been updated: this page is from an older release (useOlderReleaseRecovery). */}
      <Snackbar open={showUpdatedNotice && !updatedNoticeClosed} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <div>
          <AlertBox
            type="info"
            role="status"
            mb={0}
            sx={{ boxShadow: 3 }}
            action={
              <>
                <Button color="inherit" size="small" onClick={() => window.location.reload()}>
                  {t('Reload')}
                </Button>
                <IconButton color="inherit" size="small" aria-label={t('Close')} onClick={() => setUpdatedNoticeClosed(true)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            }
          >
            {t('TOURTASK_UPDATED_RELOAD_INFO')}
          </AlertBox>
        </div>
      </Snackbar>

      <GeneralDialog
        isOpen={dialog.open}
        onClose={() => setDialog((d) => ({ ...d, open: false }))}
        title={dialog.title}
        type={dialog.type}
      >
        <div>{dialog.content}</div>
      </GeneralDialog>

      {/* Global Loading Overlay */}
      <Backdrop open={loading} sx={{ zIndex: 9999, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </UIContext.Provider>
  );
};

export const useUI = () => {
  const context = useContext(UIContext);
  if (!context) throw new Error('useUI must be used within a UIProvider');
  return context;
};